import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowUpDown,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  FolderOpen,
  Globe,
  Link2,
  MoreHorizontal,
  Pencil,
  Pin,
  Play,
  Plus,
  RefreshCw,
  Share2,
  Tag,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Deck, Folder } from "@/types/linguadeck";
import { inspectQueryError } from "@/diagnostics";
import {
  addDecksToFolder,
  createDeck,
  DeckAlreadyImportedError,
  fetchCardCountsByDeckIds,
  fetchDeckActivitySignals,
  fetchDecksByIds,
  fetchFolderDeckIds,
  fetchFolderReviewCounts,
  fetchFolderShareToken,
  fetchGlobalDeckForStudy,
  fetchSharedFolder,
  forceReaddAllDecks,
  forceReaddSharedFolderDecks,
  getDeckCardCount,
  removeDecksFromFolder,
  shareFolder,
  smartSyncLibrary,
  smartSyncSharedFolderDecks,
  unshareFolder,
  updateFolder,
} from "@/lib/db";
import { triggerBackgroundTtsForCards } from "@/lib/triggerBackgroundTts";
import AddMaterialsModal from "./AddMaterialsModal";
import MoveDecksToFolderModal from "./MoveDecksToFolderModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  loadFolderWorkspacePrefs,
  saveFolderWorkspacePrefs,
  sortFolderDecks,
  type FolderSortOption,
} from "@/lib/folderWorkspace";
import { filterToLaunchMode, getDeckLaunchLabel, type DeckLaunchMode } from "@/lib/deckLaunch";

interface FolderViewProps {
  folderId?: string;
  sharedToken?: string;
  decks: Deck[];
  folders: Folder[];
  onSelectDeck: (id: string, mode?: "cards" | "learn" | "test" | "games") => void;
  /** Fast Track: navigate directly to Fullscreen Swipe Player, bypassing dashboard */
  onStudyDirect?: (deckId: string, mode?: DeckLaunchMode) => void;
  /** Study all cards from folder as one session. Shared: no args. Own folder: (folderId, deckIds, folderTitle?). Mode for shared study. */
  onStudyFolder?: (folderId?: string, deckIds?: string[], folderTitle?: string, mode?: DeckLaunchMode) => void;
  /** Review only due/weak cards from folder. Own folder: (folderId, deckIds, folderTitle?). */
  onReviewFolder?: (folderId: string, deckIds: string[], folderTitle?: string) => void;
  onNavTo: (view: "flashcards") => void;
  onRefreshFolders?: () => void;
  onCreateFolder?: (title: string) => Promise<Folder | null>;
  onDecksLoaded?: (decks: Deck[]) => void;
}

const SORT_OPTIONS: FolderSortOption[] = ["newest", "oldest", "alphabetical", "recent", "cards"];
type SharedModeFilter = "cards" | "learn" | "test" | "match" | "philology";

/** For shared folder: use cardCount as single source of truth; show all decks with cards in every tab.
 * For own folder: use mode-specific counts when present; fallback to cardCount when null. */
function deckHasModeContent(deck: Deck, mode: SharedModeFilter, isSharedView = false): boolean {
  const cardCount = getDeckCardCount(deck);
  if (cardCount === 0) return false;
  const modes = (deck.studyModes ?? { cards: true, learn: true, test: true, match: true, philology: true }) as Record<string, boolean>;
  if (mode === "cards") return modes.cards !== false;
  if (isSharedView) {
    return mode === "learn" || mode === "test" || mode === "match" || mode === "philology";
  }
  if (mode === "learn") return modes.learn !== false && (deck.learnableCount != null ? deck.learnableCount > 0 : cardCount > 0);
  if (mode === "test") return modes.test !== false && (deck.testableCount != null ? deck.testableCount > 0 : cardCount > 0);
  if (mode === "match") return modes.match !== false && (deck.gamesCount != null ? deck.gamesCount > 0 : cardCount > 0);
  if (mode === "philology") return modes.philology !== false && cardCount > 0;
  return false;
}

export default function FolderView({
  folderId: propFolderId,
  sharedToken,
  decks,
  folders,
  onSelectDeck,
  onStudyDirect,
  onStudyFolder,
  onReviewFolder,
  onNavTo,
  onRefreshFolders,
  onCreateFolder,
  onDecksLoaded,
}: FolderViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ folderId?: string; id?: string }>();
  const folderId = params.folderId ?? params.id ?? propFolderId ?? "";
  const { user } = useAuth();
  const isSharedView = Boolean(sharedToken);

  const [folder, setFolder] = useState<Folder | null>(null);
  const [folderTitle, setFolderTitle] = useState<string | null>(null);
  const [folderTags, setFolderTags] = useState<string[]>([]);
  const [folderDeckIds, setFolderDeckIds] = useState<string[]>([]);
  const [folderDecksFetched, setFolderDecksFetched] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddMaterials, setShowAddMaterials] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [activitySignals, setActivitySignals] = useState<Record<string, { lastStudiedAt: string | null; progressPct: number }>>({});
  const [sortOption, setSortOption] = useState<FolderSortOption>("newest");
  const [pinnedDeckIds, setPinnedDeckIds] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedDeckIds, setSelectedDeckIds] = useState<string[]>([]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [bulkBusy, setBulkBusy] = useState<"move" | "remove" | null>(null);
  const [copyFeedbackVisible, setCopyFeedbackVisible] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{ current: number; total: number } | null>(null);
  const [savingDeckId, setSavingDeckId] = useState<string | null>(null);
  const [activeSharedMode, setActiveSharedMode] = useState<SharedModeFilter>("cards");
  const [supplementDecks, setSupplementDecks] = useState<Deck[]>([]);
  const [correctedCounts, setCorrectedCounts] = useState<Record<string, number>>({});
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    description?: string;
    onConfirm: () => void | Promise<void>;
  } | null>(null);
  const [editFolderOpen, setEditFolderOpen] = useState(false);
  const [editFolderValue, setEditFolderValue] = useState("");

  const folderOwnerId = folder?.user_id ?? null;

  const openConfirm = (title: string, onConfirm: () => void | Promise<void>, description?: string) => {
    setConfirmState({ open: true, title, description, onConfirm });
  };
  const closeConfirm = () => setConfirmState(null);

  const showSyncResult = (decksAdded: number, cardsAdded: number) => {
    if (decksAdded === 0 && cardsAdded === 0) {
      toast.success(t("library.syncSuccessNothing"));
    } else if (decksAdded > 0 && cardsAdded > 0) {
      toast.success(t("library.syncSuccess", { decks: decksAdded, cards: cardsAdded }));
    } else if (decksAdded > 0) {
      toast.success(t("library.syncSuccessDecksOnly", { count: decksAdded }));
    } else {
      toast.success(t("library.syncSuccessCardsOnly", { count: cardsAdded }));
    }
  };

  const handleSmartSync = async () => {
    if (!user) {
      navigate("/auth", { state: { returnTo: window.location.pathname } });
      return;
    }
    setSyncing(true);
    setSyncProgress(null);
    try {
      const result = isSharedView
        ? await smartSyncSharedFolderDecks(folderDecks, user.id, (current, total) => setSyncProgress({ current, total }))
        : await smartSyncLibrary(folderOwnerId!, user.id);
      showSyncResult(result.decksAdded, result.cardsAdded);
      if (result.decksAdded > 0 || result.cardsAdded > 0) {
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("Smart sync failed:", err);
      toast.error(t("library.syncError"));
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  const handleForceReaddAll = async () => {
    if (!user) return;
    setSyncing(true);
    setSyncProgress(null);
    try {
      const result = isSharedView
        ? await forceReaddSharedFolderDecks(folderDecks, user.id, (current, total) => setSyncProgress({ current, total }))
        : await forceReaddAllDecks(folderOwnerId!, user.id);
      showSyncResult(result.decksAdded, result.cardsAdded);
      if (result.decksAdded > 0 || result.cardsAdded > 0) {
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("Force re-add failed:", err);
      toast.error(t("library.syncError"));
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  const handleSaveToAccount = async (deck: Deck) => {
    if (!user) {
      navigate("/auth", { state: { returnTo: window.location.pathname } });
      return;
    }
    setSavingDeckId(deck.id);
    try {
      let deckToSave = deck;
      if (!deck.cards?.length) {
        const full = await fetchGlobalDeckForStudy(deck.id);
        if (full) deckToSave = full;
      }
      const created = await createDeck(
        user.id,
        {
          title: deckToSave.title,
          lang: deckToSave.lang,
          emoji: deckToSave.emoji,
          level: deckToSave.level,
          deckType: deckToSave.deckType,
          studyModes: deckToSave.studyModes ?? { cards: true, learn: true, test: true, match: true, philology: true },
          cards: deckToSave.cards,
        },
        { sourceDeckId: deck.id }
      );
      if (created.cards?.length) {
        triggerBackgroundTtsForCards(created.cards, created.lang ?? "en");
      }
      toast.success(t("library.syncSuccessDecksOnly", { count: 1 }));
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof DeckAlreadyImportedError) {
        toast.info(t("shared.deckAlreadyInLibrary"));
      } else {
        console.error("Failed to save deck:", err);
        toast.error(t("library.syncError"));
      }
      setSavingDeckId(null);
    }
  };

  const loadFolder = useCallback(async () => {
    setLoading(true);
    if (sharedToken) {
      try {
        const sharedFolder = await fetchSharedFolder(sharedToken);
        if (!sharedFolder) {
          setFolder(null);
          setFolderTitle(null);
          setFolderTags([]);
          setFolderDeckIds([]);
          setFolderDecksFetched([]);
        } else {
          setFolder(sharedFolder.folder);
          setFolderTitle(sharedFolder.folder.title);
          setFolderTags(sharedFolder.folder.tags ?? []);
          setFolderDeckIds(sharedFolder.decks.map((deck) => deck.id));
          setFolderDecksFetched(sharedFolder.decks);
        }
      } catch (err) {
        console.error("Error fetching shared folder:", err);
        setFolder(null);
        setFolderTitle(null);
        setFolderTags([]);
        setFolderDeckIds([]);
        setFolderDecksFetched([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!folderId) {
      setLoading(false);
      return;
    }

    try {
      const [{ data, error }, sharedFolderToken, deckIds] = await Promise.all([
        supabase
          .from("folders")
          .select("id, user_id, title, created_at, tags")
          .eq("id", folderId)
          .single(),
        fetchFolderShareToken(folderId),
        fetchFolderDeckIds(folderId),
      ]);

      const folderRow = data;

      if (error || !folderRow) {
        setFolder(null);
        setFolderTitle(null);
        setFolderTags([]);
        setFolderDeckIds([]);
        setFolderDecksFetched([]);
      } else {
        const mappedFolder: Folder = {
          id: folderRow.id,
          user_id: folderRow.user_id,
          title: folderRow.title,
          created_at: folderRow.created_at,
          tags: Array.isArray(folderRow.tags) ? folderRow.tags.filter((value): value is string => typeof value === "string") : [],
          shareToken: sharedFolderToken,
        };
        setFolder(mappedFolder);
        setFolderTitle(mappedFolder.title);
        setFolderTags(mappedFolder.tags ?? []);
        setFolderDeckIds(deckIds);
        const loadIsOwner = user?.id && String(user.id) === String(mappedFolder.user_id);
        if (loadIsOwner && deckIds.length > 0) {
          setFolderDecksFetched([]);
        } else if (!loadIsOwner && deckIds.length > 0) {
          const decksInFolder = await fetchDecksByIds(deckIds);
          setFolderDecksFetched(decksInFolder);
        } else {
          setFolderDecksFetched([]);
        }
      }
    } catch (err) {
      inspectQueryError({
        queryName: "loadFolder",
        flow: "folder.load",
        table: "folders",
        filters: { folderId },
        responseError: err,
      });
      console.error("Error fetching folder:", err);
      setFolder(null);
      setFolderTitle(null);
    } finally {
      setLoading(false);
    }
  }, [folderId, sharedToken, user?.id]);

  useEffect(() => {
    void loadFolder();
  }, [loadFolder]);

  const ownsFolder = folder?.user_id === user?.id;
  const canManage = ownsFolder && !isSharedView;

  // Supplement: fetch missing decks (e.g. paginated out of state.decks) and fix cardCount for decks with 0
  useEffect(() => {
    if (!canManage || folderDeckIds.length === 0) {
      setSupplementDecks([]);
      setCorrectedCounts({});
      return;
    }
    let cancelled = false;
    const run = async () => {
      const baseDecks = decks.filter((d) => folderDeckIds.includes(d.id));
      const inBase = new Set(baseDecks.map((d) => d.id));
      const missingIds = folderDeckIds.filter((id) => !inBase.has(id));
      const zeroCountIds = baseDecks.filter((d) => getDeckCardCount(d) === 0).map((d) => d.id);

      if (missingIds.length > 0) {
        const fetched = await fetchDecksByIds(missingIds);
        if (!cancelled) setSupplementDecks(fetched);
      } else {
        setSupplementDecks([]);
      }

      if (zeroCountIds.length > 0) {
        const counts = await fetchCardCountsByDeckIds(zeroCountIds);
        const next: Record<string, number> = {};
        for (const [id, n] of counts) if (n > 0) next[id] = n;
        if (!cancelled) setCorrectedCounts(next);
      } else {
        setCorrectedCounts({});
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [canManage, folderDeckIds, decks]);

  const folderDecks = useMemo(() => {
    if (!canManage) return folderDecksFetched;
    const base = decks.filter((deck) => folderDeckIds.includes(deck.id));
    const supplementById = new Map(supplementDecks.map((d) => [d.id, d]));
    const orderIdx = new Map(folderDeckIds.map((id, i) => [id, i]));
    const combined: Deck[] = [];
    for (const id of folderDeckIds) {
      const d = base.find((x) => x.id === id) ?? supplementById.get(id);
      if (d) combined.push(d);
    }
    return combined.map((deck) => {
      const corrected = correctedCounts[deck.id];
      if (corrected != null) return { ...deck, cardCount: corrected };
      return deck;
    });
  }, [canManage, decks, folderDeckIds, folderDecksFetched, supplementDecks, correctedCounts]);

  useEffect(() => {
    if (!folderId) return;
    const prefs = loadFolderWorkspacePrefs(user?.id ?? null, folderId);
    setSortOption(prefs.sort);
    setPinnedDeckIds(prefs.pinnedDeckIds);
  }, [folderId, user?.id]);

  useEffect(() => {
    onDecksLoaded?.(folderDecks);
  }, [folderDecks, onDecksLoaded]);

  useEffect(() => {
    if (!copyFeedbackVisible) return;
    const timeoutId = window.setTimeout(() => setCopyFeedbackVisible(false), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [copyFeedbackVisible]);

  useEffect(() => {
    if (!folderId) return;
    saveFolderWorkspacePrefs(user?.id ?? null, folderId, {
      sort: sortOption,
      pinnedDeckIds,
    });
  }, [folderId, pinnedDeckIds, sortOption, user?.id]);

  useEffect(() => {
    setSelectedDeckIds((prev) => prev.filter((id) => folderDecks.some((deck) => deck.id === id)));
  }, [folderDecks]);

  useEffect(() => {
    if (!selectionMode) {
      setSelectedDeckIds([]);
    }
  }, [selectionMode]);

  useEffect(() => {
    if (!user?.id || folderDecks.length === 0) {
      setActivitySignals({});
      return;
    }

    let cancelled = false;
    (async () => {
      const signals = await fetchDeckActivitySignals(user.id, folderDecks.map((deck) => deck.id));
      if (!cancelled) {
        setActivitySignals(signals);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [folderDecks, user?.id]);

  const sortedDecks = useMemo(
    () => sortFolderDecks(folderDecks, sortOption, pinnedDeckIds, activitySignals),
    [activitySignals, folderDecks, pinnedDeckIds, sortOption]
  );

  const displayedDecks = useMemo(() => {
    if (!isSharedView) return sortedDecks;
    return sortedDecks.filter((deck) => deckHasModeContent(deck, activeSharedMode, true));
  }, [isSharedView, sortedDecks, activeSharedMode]);

  const [reviewCountByDeckFetched, setReviewCountByDeckFetched] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!canManage || folderDeckIds.length === 0 || !user?.id) {
      setReviewCountByDeckFetched({});
      return;
    }
    fetchFolderReviewCounts(user.id, folderDeckIds).then(setReviewCountByDeckFetched).catch(() => setReviewCountByDeckFetched({}));
  }, [canManage, folderDeckIds, user?.id]);

  const dueCountByDeck = useMemo(() => {
    return Object.fromEntries(
      folderDecks.map((deck) => [deck.id, reviewCountByDeckFetched[deck.id] ?? 0])
    ) as Record<string, number>;
  }, [folderDecks, reviewCountByDeckFetched]);

  const totalCards = useMemo(
    () => folderDecks.reduce((sum, deck) => sum + getDeckCardCount(deck), 0),
    [folderDecks]
  );
  const totalDueCards = useMemo(
    () => Object.values(dueCountByDeck).reduce((sum, count) => sum + count, 0),
    [dueCountByDeck]
  );
  const todayKey = new Date().toISOString().slice(0, 10);
  const studiedTodayDecks = useMemo(
    () =>
      Object.values(activitySignals).filter(
        (signal) => typeof signal.lastStudiedAt === "string" && signal.lastStudiedAt.slice(0, 10) === todayKey
      ).length,
    [activitySignals, todayKey]
  );
  const overallProgressPct = useMemo(() => {
    if (folderDecks.length === 0) return 0;
    const total = folderDecks.reduce((sum, deck) => sum + (activitySignals[deck.id]?.progressPct ?? 0), 0);
    return Math.round(total / folderDecks.length);
  }, [activitySignals, folderDecks]);

  const selectedCount = selectedDeckIds.length;
  const canBulkSelect = canManage && folderDecks.length > 0;
  const hasCards = (deck: Deck) => getDeckCardCount(deck) > 0;
  const firstStudyDeck = sortedDecks.find(hasCards) ?? null;
  const totalFolderCards = useMemo(
    () => folderDecks.reduce((sum, deck) => sum + getDeckCardCount(deck), 0),
    [folderDecks]
  );
  const canStudyFolder = totalFolderCards > 0;
  const selectedStudyDeck = sortedDecks.find((deck) => selectedDeckIds.includes(deck.id) && hasCards(deck)) ?? null;
  const moveFolders = useMemo(
    () => folders.filter((entry) => entry.id !== folderId),
    [folderId, folders]
  );

  const toggleDeckSelection = (deckId: string) => {
    setSelectedDeckIds((prev) =>
      prev.includes(deckId) ? prev.filter((id) => id !== deckId) : [...prev, deckId]
    );
  };

  const toggleDeckPin = (deckId: string) => {
    setPinnedDeckIds((prev) =>
      prev.includes(deckId) ? prev.filter((id) => id !== deckId) : [deckId, ...prev.filter((id) => id !== deckId)]
    );
  };

  const handleEdit = useCallback(() => {
    if (!folderId || !folderTitle) return;
    setEditFolderValue(folderTitle);
    setEditFolderOpen(true);
  }, [folderId, folderTitle]);

  const handleEditFolderSubmit = useCallback(async () => {
    if (!folderId || !editFolderValue?.trim() || editFolderValue.trim() === folderTitle) {
      setEditFolderOpen(false);
      return;
    }
    try {
      await updateFolder(folderId, { title: editFolderValue.trim() });
      setFolderTitle(editFolderValue.trim());
      onRefreshFolders?.();
      setEditFolderOpen(false);
    } catch (err) {
      console.error("Failed to rename folder:", err);
    }
  }, [folderId, editFolderValue, folderTitle, onRefreshFolders]);

  const copyToClipboardWithFocusRetry = useCallback(async (url: string): Promise<boolean> => {
    const doCopy = async (): Promise<boolean> => {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return true;
      }
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      textarea.setAttribute("aria-hidden", "true");
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    };
    try {
      await doCopy();
      return true;
    } catch (e) {
      const isNotAllowed = (err: unknown) =>
        err instanceof Error && err.name === "NotAllowedError";
      if (isNotAllowed(e)) {
        await new Promise((r) => setTimeout(r, 200));
        window.focus();
        try {
          await doCopy();
          return true;
        } catch (e2) {
          if (isNotAllowed(e2)) {
            try {
              document.body.focus();
              const textarea = document.createElement("textarea");
              textarea.value = url;
              textarea.style.position = "fixed";
              textarea.style.left = "-9999px";
              textarea.setAttribute("aria-hidden", "true");
              document.body.appendChild(textarea);
              textarea.select();
              const ok = document.execCommand("copy");
              document.body.removeChild(textarea);
              return ok;
            } catch {
              return false;
            }
          }
          return false;
        }
      }
      return false;
    }
  }, []);

  const handleShare = useCallback(async () => {
    const idToShare = folder?.id ?? folderId;
    if (!idToShare) return;
    try {
      const token =
        typeof folder?.shareToken === "string" && folder.shareToken.trim()
          ? folder.shareToken
          : await shareFolder(idToShare);
      const url = `${window.location.origin}/share/folder/${token}`;
      const copied = await copyToClipboardWithFocusRetry(url);
      if (copied) {
        setCopyFeedbackVisible(true);
        setFolder((prev) => (prev ? { ...prev, shareToken: token } : prev));
        onRefreshFolders?.();
        toast.success(t("folder.linkCopied"));
      } else {
        toast.error(t("folder.linkCopyError"));
      }
    } catch (err) {
      console.error("Failed to create share link:", err);
      const msg =
        (err as Error)?.message === "FOLDER_SHARING_NOT_CONFIGURED"
          ? t("folder.shareNotConfigured")
          : t("folder.linkCopyError");
      toast.error(msg);
    }
  }, [folder?.id, folder?.shareToken, folderId, copyToClipboardWithFocusRetry, onRefreshFolders, t]);

  const handleUnshare = useCallback(() => {
    const idToUnshare = folder?.id ?? folderId;
    if (!idToUnshare) return;
    openConfirm(t("folder.unshare") + "?", async () => {
      try {
        await unshareFolder(idToUnshare);
        setFolder((prev) => (prev ? { ...prev, shareToken: null } : prev));
        onRefreshFolders?.();
        toast.success(t("folder.linkRemoved"));
      } catch (err) {
        console.error("Failed to remove share link:", err);
        toast.error(t("folder.unshareError"));
        throw err;
      }
    });
  }, [folder?.id, folderId, onRefreshFolders, t]);

  const handleDelete = useCallback(() => {
    const idToDelete = folderId || folder?.id;
    if (!idToDelete) return;
    openConfirm(t("folder.deleteConfirm"), async () => {
      try {
        await supabase.from("folders").delete().eq("id", idToDelete);
        onRefreshFolders?.();
        navigate("/", { replace: true });
        toast.success(t("folder.deleted"));
      } catch (err) {
        console.error("Failed to delete folder:", err);
        toast.error(t("folder.deleteError"));
        throw err;
      }
    });
  }, [folder?.id, folderId, navigate, onRefreshFolders, t]);

  const handleOpenTagModal = useCallback(() => {
    setTagInput(folderTags?.join(", ") || "");
    setIsTagModalOpen(true);
  }, [folderTags]);

  const handleSaveTags = useCallback(async () => {
    const id = folderId || folder?.id;
    if (!id) return;
    const nextTags = tagInput
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    try {
      const { error } = await supabase
        .from("folders")
        .update({ tags: nextTags })
        .eq("id", id);
      if (error) throw error;
      setFolder((prev) => (prev ? { ...prev, tags: nextTags } : null));
      setFolderTags(nextTags);
      onRefreshFolders?.();
      setIsTagModalOpen(false);
      setTagInput("");
    } catch (err) {
      console.error("Error saving tags:", err);
      toast.error(t("folder.tagsSaveError"));
    }
  }, [folder?.id, folderId, onRefreshFolders, t, tagInput]);

  const launchMode: DeckLaunchMode = filterToLaunchMode(activeSharedMode);

  const handleStudyDeck = (deckId: string) => {
    if (!deckId) return;
    if (onStudyDirect) {
      onStudyDirect(deckId, isSharedView ? launchMode : undefined);
      return;
    }
    onSelectDeck(deckId, isSharedView ? launchMode : undefined);
    onNavTo("flashcards");
  };

  const handleStudySelected = () => {
    if (!selectedStudyDeck) return;
    handleStudyDeck(selectedStudyDeck.id);
    setSelectionMode(false);
  };

  const handleRemoveSelected = () => {
    if (!folderId || selectedDeckIds.length === 0) return;
    openConfirm(t("folder.bulkRemoveConfirm", { count: selectedDeckIds.length }), async () => {
      setBulkBusy("remove");
      try {
        await removeDecksFromFolder(folderId, selectedDeckIds);
        toast.success(t("folder.bulkRemoveSuccess", { count: selectedDeckIds.length }));
        setSelectedDeckIds([]);
        setSelectionMode(false);
        await loadFolder();
        onRefreshFolders?.();
      } catch (err) {
        console.error("Failed to remove decks from folder:", err);
        toast.error(t("folder.addMaterialsError"));
      } finally {
        setBulkBusy(null);
      }
    });
  };

  const handleMoveSelected = async (targetFolderId: string) => {
    if (!folderId || selectedDeckIds.length === 0) return;
    setBulkBusy("move");
    try {
      await addDecksToFolder(targetFolderId, selectedDeckIds);
      await removeDecksFromFolder(folderId, selectedDeckIds);
      toast.success(t("folder.bulkMoveSuccess", { count: selectedDeckIds.length }));
      setSelectedDeckIds([]);
      setSelectionMode(false);
      setShowMoveModal(false);
      await loadFolder();
      onRefreshFolders?.();
    } catch (err) {
      console.error("Failed to move decks between folders:", err);
      toast.error(t("folder.addMaterialsError"));
    } finally {
      setBulkBusy(null);
    }
  };

  const handleCreateFolderForMove = async (title: string): Promise<Folder | null> => {
    if (!onCreateFolder) return null;
    const created = await onCreateFolder(title);
    if (created) {
      onRefreshFolders?.();
    }
    return created;
  };

  if (loading) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-[hsl(var(--muted-foreground))]">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--bg3))]">
          <FolderOpen className="h-7 w-7 animate-pulse text-[hsl(var(--primary))]" />
        </div>
        <div className="text-sm">{t("folder.loading")}</div>
      </div>
    );
  }

  if (!folderTitle) {
    return (
      <div className="py-16 text-center text-[hsl(var(--muted-foreground))]">
        <div className="mb-4 text-5xl">📁</div>
        <p>{t("folder.notFound")}</p>
        <button
          type="button"
          onClick={() => navigate(user ? "/" : "/landing")}
          className="mt-4 rounded-full bg-[hsl(var(--primary))] px-5 py-2.5 text-sm font-medium text-white"
        >
          {t("folderView.goHome")}
        </button>
      </div>
    );
  }

  return (
    <div className={selectionMode && selectedCount > 0 ? "pb-28 md:pb-24" : ""}>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[hsl(var(--primary))]/15 to-[hsl(var(--accent2))]/15 text-[hsl(var(--primary))]">
              <FolderOpen size={24} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate font-[Instrument_Serif] text-3xl leading-none text-[hsl(var(--foreground))]">
                  {folderTitle}
                </h2>
                {selectionMode && (
                  <span className="inline-flex items-center rounded-full border border-[hsl(var(--primary))]/20 bg-[hsl(var(--primary))]/10 px-2.5 py-1 text-xs font-semibold text-[hsl(var(--primary))]">
                    {t("folder.selectionMode")}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                {t("folder.deckCount", { count: folderDecks.length })}
              </p>
              {folderTags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {folderTags.map((tag, index) => (
                    <span
                      key={`${tag}-${index}`}
                      className="inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 self-start">
          {(canManage || copyFeedbackVisible) && (
            <div className="flex flex-col items-start gap-2 sm:items-end">
              {canManage && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex h-10 w-10 items-center justify-center rounded-full border-none bg-slate-200 text-slate-800 transition-colors hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                      <MoreHorizontal size={20} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={8}
                    className="mt-2 w-56 rounded-xl border border-gray-100 bg-white py-1 shadow-lg dark:border-slate-800 dark:bg-slate-900"
                  >
                    <DropdownMenuItem
                      onClick={handleEdit}
                      className="cursor-pointer rounded-none border-none px-4 py-2.5 text-sm font-medium text-gray-900 transition-colors outline-none hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 focus:text-gray-900 dark:text-gray-100 dark:hover:bg-slate-700 dark:hover:text-gray-100 dark:focus:bg-slate-700 dark:focus:text-gray-100"
                    >
                      <Pencil className="mr-3 h-4 w-4 shrink-0 text-gray-600 dark:text-gray-300" />
                      {t("folder.edit")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => void handleShare()}
                      className="cursor-pointer rounded-none border-none px-4 py-2.5 text-sm font-medium text-gray-900 transition-colors outline-none hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 focus:text-gray-900 dark:text-gray-100 dark:hover:bg-slate-700 dark:hover:text-gray-100 dark:focus:bg-slate-700 dark:focus:text-gray-100"
                    >
                      {folder?.shareToken ? (
                        <>
                          <Link2 className="mr-3 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                          {t("folder.shareActive")}
                        </>
                      ) : (
                        <>
                          <Share2 className="mr-3 h-4 w-4 shrink-0 text-gray-600 dark:text-gray-300" />
                          {t("folder.share")}
                        </>
                      )}
                    </DropdownMenuItem>
                    {folder?.shareToken && (
                      <DropdownMenuItem
                        onSelect={() => void handleUnshare()}
                        className="cursor-pointer rounded-none border-none px-4 py-2.5 text-sm font-medium text-red-700 transition-colors outline-none hover:bg-red-50 hover:text-red-800 focus:bg-red-50 focus:text-red-800 dark:text-red-300 dark:hover:bg-red-950/40 dark:hover:text-red-200 dark:focus:bg-red-950/40 dark:focus:text-red-200"
                      >
                        <Trash2 className="mr-3 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                        {t("folder.unshare")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onSelect={handleOpenTagModal}
                      className="cursor-pointer rounded-none border-none px-4 py-2.5 text-sm font-medium text-gray-900 transition-colors outline-none hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 focus:text-gray-900 dark:text-gray-100 dark:hover:bg-slate-700 dark:hover:text-gray-100 dark:focus:bg-slate-700 dark:focus:text-gray-100"
                    >
                      <Tag className="mr-3 h-4 w-4 shrink-0 text-gray-600 dark:text-gray-300" />
                      {t("folder.addTags")}
                    </DropdownMenuItem>
                    <div className="my-1 h-px bg-gray-100 dark:bg-slate-700" />
                    <DropdownMenuItem
                      onSelect={() => void handleDelete()}
                      className="cursor-pointer rounded-none border-none px-4 py-2.5 text-sm font-medium text-red-700 transition-colors outline-none hover:bg-red-50 hover:text-red-800 focus:bg-red-50 focus:text-red-800 dark:text-red-300 dark:hover:bg-red-950/40 dark:hover:text-red-200 dark:focus:bg-red-950/40 dark:focus:text-red-200"
                    >
                      <Trash2 className="mr-3 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                      {t("folder.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {copyFeedbackVisible && (
                <span className="inline-flex max-w-full items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
                  {t("folder.linkCopied")}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 xl:grid-cols-5">
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-4 shadow-sm">
          <div className="text-2xl font-bold text-[hsl(var(--foreground))]">{folderDecks.length}</div>
          <div className="mt-1 text-xs font-medium text-[hsl(var(--muted-foreground))]">
            {t("folder.summaryDecks")}
          </div>
        </div>
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-4 shadow-sm">
          <div className="text-2xl font-bold text-[hsl(var(--foreground))]">{totalCards}</div>
          <div className="mt-1 text-xs font-medium text-[hsl(var(--muted-foreground))]">
            {t("folder.summaryCards")}
          </div>
        </div>
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-4 shadow-sm">
          <div className="text-2xl font-bold text-[hsl(var(--foreground))]">{totalDueCards}</div>
          <div className="mt-1 text-xs font-medium text-[hsl(var(--muted-foreground))]">
            {t("folder.summaryDue")}
          </div>
        </div>
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-4 shadow-sm">
          <div className="text-2xl font-bold text-[hsl(var(--foreground))]">{studiedTodayDecks}</div>
          <div className="mt-1 text-xs font-medium text-[hsl(var(--muted-foreground))]">
            {t("folder.summaryStudiedToday")}
          </div>
        </div>
        <div className="col-span-2 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-4 shadow-sm xl:col-span-1">
          <div className="flex items-center justify-between gap-3">
            <div className="text-2xl font-bold text-[hsl(var(--foreground))]">{overallProgressPct}%</div>
            <span className="text-xs font-medium text-[hsl(var(--muted-foreground))]">
              {t("folder.summaryProgress")}
            </span>
          </div>
          <div className="mt-3 h-2 rounded-full bg-[hsl(var(--bg3))]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] transition-all"
              style={{ width: `${Math.min(100, Math.max(overallProgressPct, folderDecks.length > 0 ? 4 : 0))}%` }}
            />
          </div>
        </div>
      </div>

      {isSharedView && (
        <div className="mb-4 flex flex-wrap gap-2">
          {(["cards", "learn", "test", "match", "philology"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setActiveSharedMode(mode)}
              className={`h-9 sm:h-10 inline-flex items-center justify-center gap-2 rounded-xl border px-3 sm:px-4 text-sm font-medium transition-all duration-150 ${
                activeSharedMode === mode
                  ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))] shadow-sm active:scale-[0.98]"
                  : "bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:bg-[hsl(var(--border))]/10 active:scale-[0.98]"
              }`}
            >
              {mode === "match" ? t("home.tabsGames") : t(`deckModal.studyMode${mode.charAt(0).toUpperCase() + mode.slice(1)}`)}
            </button>
          ))}
        </div>
      )}

      <div className="mb-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <label className="inline-flex items-center gap-2 rounded-full bg-neutral-100 dark:bg-[hsl(var(--bg3))] px-4 py-2.5 text-sm text-[hsl(var(--foreground))] transition-colors duration-200 hover:bg-neutral-200 dark:hover:bg-[hsl(var(--bg3))]/80">
              <ArrowUpDown size={16} className="text-[hsl(var(--muted-foreground))] shrink-0" />
              <span className="text-xs font-medium uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                {t("folder.sortLabel")}
              </span>
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value as FolderSortOption)}
                className="min-w-[140px] border-none bg-transparent text-sm font-medium text-[hsl(var(--foreground))] outline-none cursor-pointer"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {t(`folder.sort.${option}`)}
                  </option>
                ))}
              </select>
            </label>
            {canBulkSelect && (
              <button
                type="button"
                onClick={() => setSelectionMode((prev) => !prev)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  selectionMode
                    ? "bg-[hsl(var(--primary))] text-white"
                    : "bg-neutral-100 dark:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-neutral-200 dark:hover:bg-[hsl(var(--bg3))]/80"
                }`}
              >
                <CheckCircle2 size={16} />
                {selectionMode ? t("folder.exitSelection") : t("folder.selectionMode")}
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => setShowAddMaterials(true)}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium bg-neutral-100 dark:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-neutral-200 dark:hover:bg-[hsl(var(--bg3))]/80 transition-colors duration-200"
              >
                <Plus size={16} />
                {t("folder.addMaterials")}
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isSharedView && user && folderDecks.length > 0 && folderOwnerId && (
              <div className="flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleSmartSync}
                      disabled={syncing}
                      className="inline-flex h-11 items-center gap-2 rounded-xl bg-[hsl(var(--primary))] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:opacity-95 disabled:cursor-wait disabled:opacity-60"
                    >
                      {syncing ? (
                        <>
                          <span
                            style={{
                              width: 14,
                              height: 14,
                              border: "2px solid rgba(255,255,255,.3)",
                              borderTopColor: "white",
                              borderRadius: "50%",
                              animation: "spin 0.8s linear infinite",
                            }}
                          />
                          {syncProgress && syncProgress.total > 0
                            ? t("library.syncingProgress", {
                                percent: Math.round((syncProgress.current / syncProgress.total) * 100),
                              })
                            : t("library.syncing")}
                        </>
                      ) : (
                        <><RefreshCw size={14} /> {t("library.syncToMyLibrary")}</>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("library.syncToMyLibraryTooltip")}</p>
                  </TooltipContent>
                </Tooltip>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      disabled={syncing}
                      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--bg3))]/80 disabled:cursor-wait disabled:opacity-60"
                      aria-label={t("library.forceReaddAll")}
                    >
                      <ChevronDown size={18} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="min-w-0 max-w-[100vw] w-full"
                    style={{
                      background: "hsl(var(--card-bg))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "var(--radius)",
                      padding: 4,
                    }}
                  >
                    <DropdownMenuItem
                      onClick={handleForceReaddAll}
                      disabled={syncing}
                      className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                      style={{
                        fontSize: 13,
                        padding: "8px 12px",
                        cursor: syncing ? "wait" : "pointer",
                      }}
                    >
                      {t("library.forceReaddAll")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                if (isSharedView && onStudyFolder) {
                  onStudyFolder(undefined, undefined, undefined, launchMode);
                } else if (folderId && folderDeckIds.length > 0 && onStudyFolder) {
                  if (!canStudyFolder) {
                    toast.error(t("folder.noCardsToStudy"));
                    return;
                  }
                  onStudyFolder(folderId, folderDeckIds, folderTitle ?? undefined);
                }
              }}
              disabled={isSharedView ? !onStudyFolder : !canStudyFolder || !onStudyFolder}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 ease-out hover:opacity-95 hover:scale-[1.02] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
            >
              <Play size={15} />
              {t("folder.studyFolder")}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!isSharedView && folderId && folderDeckIds.length > 0 && onReviewFolder && totalDueCards > 0) {
                  onReviewFolder(folderId, folderDeckIds, folderTitle ?? undefined);
                } else if (totalDueCards === 0 && !isSharedView) {
                  toast.error(t("folder.noCardsToReview"));
                }
              }}
              disabled={isSharedView || totalDueCards === 0 || !onReviewFolder}
              className="inline-flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] px-4 py-2.5 text-sm font-medium text-[hsl(var(--foreground))] transition-colors hover:bg-[hsl(var(--bg3))]/70 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <BookOpen size={15} />
              {t("folder.reviewDue")}
            </button>
          </div>
        </div>
      </div>

      {(isSharedView ? displayedDecks : sortedDecks).length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] px-6 py-16 text-center">
          <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-[1.25rem] bg-gradient-to-br from-[hsl(var(--primary))]/15 to-[hsl(var(--accent2))]/10">
            <FolderOpen size={40} className="text-[hsl(var(--primary))]" />
          </div>
          <div className="mb-2 text-lg font-semibold text-[hsl(var(--foreground))]">
            {t("folder.empty")}
          </div>
          {canManage ? (
            <>
              <p className="mb-6 text-sm text-[hsl(var(--muted-foreground))]">
                {t("folder.addMaterialsHint")}
              </p>
              <button
                type="button"
                onClick={() => setShowAddMaterials(true)}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-[hsl(var(--primary))]/25"
              >
                <Plus size={18} />
                {t("folder.addMaterials")}
              </button>
            </>
          ) : (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t("folder.emptyViewer")}</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {(isSharedView ? displayedDecks : sortedDecks).map((deck) => {
            const isSelected = selectedDeckIds.includes(deck.id);
            const isPinned = pinnedDeckIds.includes(deck.id);
            const progressPct = activitySignals[deck.id]?.progressPct ?? 0;
            const dueCount = dueCountByDeck[deck.id] ?? 0;
            const cardCount = getDeckCardCount(deck);
            const openDeck = () =>
              onSelectDeck(deck.id, isSharedView ? launchMode : deck.type === "tests" ? "test" : undefined);

            return (
              <div
                key={deck.id}
                className={`relative flex flex-col gap-2.5 md:gap-4 rounded-2xl border bg-[hsl(var(--card-bg))] p-3.5 md:p-5 shadow-sm transition-all duration-200 ease-out ${
                  selectionMode
                    ? "cursor-pointer hover:border-[hsl(var(--primary))]"
                    : "hover:-translate-y-0.5 hover:border-neutral-300 dark:hover:border-neutral-600 hover:shadow-lg"
                } ${isSelected ? "border-[hsl(var(--primary))] ring-2 ring-[hsl(var(--primary))]/20" : "border-[hsl(var(--border))]"}`}
                onClick={selectionMode ? () => toggleDeckSelection(deck.id) : undefined}
              >
                <div className="absolute inset-x-0 top-0 h-[2px] rounded-t-2xl bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))]" />
                <div className="flex items-start justify-between gap-2 md:gap-3">
                  <div className="flex min-w-0 items-start gap-2 md:gap-3">
                    {selectionMode && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDeckSelection(deck.id);
                        }}
                        className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-white transition-colors ${
                          isSelected
                            ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]"
                            : "border-[hsl(var(--border))] bg-transparent"
                        }`}
                        aria-label={t("folder.selectionMode")}
                      >
                        {isSelected && <CheckCircle2 size={14} />}
                      </button>
                    )}
                    <div className="flex h-10 w-10 md:h-11 md:w-11 shrink-0 items-center justify-center rounded-xl md:rounded-2xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
                      <BookOpen size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-semibold leading-snug text-[hsl(var(--foreground))]">
                        {deck.title}
                      </h3>
                      <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">
                        {t("sidebar.cardsCount", { count: cardCount })}
                      </p>
                    </div>
                  </div>

                  {canManage && !selectionMode && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDeckPin(deck.id);
                      }}
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                        isPinned
                          ? "border-amber-300 bg-amber-50 text-amber-600 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                          : "border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                      }`}
                      aria-label={isPinned ? t("folder.unpinDeck") : t("folder.pinDeck")}
                    >
                      <Pin size={15} className={isPinned ? "fill-current" : ""} />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                  <span className="inline-flex items-center gap-1 rounded-md bg-[hsl(var(--bg3))] px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    <Globe size={10} className="shrink-0 opacity-80" />
                    {deck.lang.toUpperCase()}
                    {deck.level ? ` · ${deck.level}` : ""}
                  </span>
                  {isPinned && (
                    <span className="inline-flex items-center gap-1 rounded-lg bg-amber-50/80 px-2.5 py-0.5 text-[11px] font-medium text-amber-700/90 dark:bg-amber-500/10 dark:text-amber-400/90">
                      <Pin size={11} className="fill-current" />
                      {t("folder.pinned")}
                    </span>
                  )}
                  {dueCount > 0 && (
                    <span className="inline-flex items-center rounded-lg bg-emerald-50/80 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700/90 dark:bg-emerald-500/10 dark:text-emerald-400/90">
                      {t("folder.cardCountDue", { count: dueCount })}
                    </span>
                  )}
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between gap-3 text-xs font-medium text-[hsl(var(--muted-foreground))]">
                    <span>{t("folder.summaryProgress")}</span>
                    <span>{progressPct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-[hsl(var(--bg3))]">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] transition-all"
                      style={{ width: `${Math.min(100, Math.max(progressPct, cardCount > 0 ? 4 : 0))}%` }}
                    />
                  </div>
                </div>

                {!selectionMode && (
                  <div className="mt-auto flex flex-col gap-2 md:gap-3 pt-0">
                    {cardCount > 0 && onStudyDirect ? (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStudyDeck(deck.id);
                          }}
                          className={`inline-flex h-10 md:h-11 w-full items-center justify-center gap-2 rounded-xl md:rounded-2xl border-0 px-4 text-sm font-semibold text-white shadow-sm transition-all duration-150 ease-out hover:opacity-95 hover:shadow-md hover:scale-[1.02] active:scale-[0.98] ${
                            launchMode === "games"
                              ? "bg-gradient-to-r from-orange-500 to-orange-400 hover:from-orange-600 hover:to-orange-500"
                              : "bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))]"
                          }`}
                        >
                          <Play size={16} className="shrink-0" />{" "}
                          {isSharedView ? getDeckLaunchLabel(t, launchMode, deck.type === "tests" ? "tests" : undefined) : t("deckDashboard.startCards")}
                        </button>
                        {isSharedView && user && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveToAccount(deck);
                            }}
                            disabled={savingDeckId === deck.id}
                            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-emerald-200/80 bg-emerald-50/80 px-4 text-sm font-medium text-emerald-600/90 transition-colors duration-150 hover:bg-emerald-100/80 active:scale-[0.98] disabled:cursor-wait disabled:opacity-60 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400/90 dark:hover:bg-emerald-500/15"
                          >
                            <Download size={16} className="shrink-0" />
                            {savingDeckId === deck.id ? t("library.syncing") : t("library.saveToAccount")}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeck();
                          }}
                          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl md:rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] px-4 text-sm font-medium text-[hsl(var(--muted-foreground))] transition-all duration-150 hover:bg-[hsl(var(--bg3))]/80 hover:text-[hsl(var(--foreground))] active:scale-[0.98]"
                        >
                          <ChevronRight size={16} className="shrink-0" /> {t("home.goToModule")}
                        </button>
                      </>
                    ) : (
                      <>
                        {isSharedView && user && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveToAccount(deck);
                            }}
                            disabled={savingDeckId === deck.id}
                            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-emerald-200/80 bg-emerald-50/80 px-4 text-sm font-medium text-emerald-600/90 transition-colors duration-150 hover:bg-emerald-100/80 active:scale-[0.98] disabled:cursor-wait disabled:opacity-60 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400/90 dark:hover:bg-emerald-500/15"
                          >
                            <Download size={16} className="shrink-0" />
                            {savingDeckId === deck.id ? t("library.syncing") : t("library.saveToAccount")}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeck();
                          }}
                          className="inline-flex h-10 md:h-11 w-full items-center justify-center gap-2 rounded-xl md:rounded-2xl border-0 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] px-4 text-sm font-semibold text-white shadow-sm transition-all duration-150 ease-out hover:opacity-95 hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <ChevronRight size={16} className="shrink-0" /> {t("home.goToModule")}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {canManage && !selectionMode && (
            <button
              type="button"
              onClick={() => setShowAddMaterials(true)}
              className="flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[hsl(var(--border))] bg-transparent px-5 text-center transition-all hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5"
            >
              <Plus size={30} className="text-[hsl(var(--muted-foreground))]" />
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                {t("folder.addMaterials")}
              </span>
            </button>
          )}
        </div>
      )}

      {selectionMode && selectedCount > 0 && (
        <div className="fixed inset-x-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-40 md:left-1/2 md:right-auto md:w-[min(760px,calc(100vw-3rem))] md:-translate-x-1/2 md:bottom-6">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-white/95 p-3 shadow-2xl backdrop-blur dark:bg-[hsl(var(--card-bg))]/95">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-sm font-semibold text-[hsl(var(--foreground))]">
                {t("folder.selectedCount", { count: selectedCount })}
              </div>
              <div className="grid grid-cols-3 gap-2 md:flex md:flex-wrap">
                <button
                  type="button"
                  onClick={handleStudySelected}
                  disabled={!selectedStudyDeck}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent2))] px-3 py-2.5 text-sm font-semibold text-white transition-all duration-200 ease-out hover:opacity-95 hover:scale-[1.02] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                >
                  <Play size={15} />
                  {t("folder.bulkStudy")}
                </button>
                <button
                  type="button"
                  onClick={() => setShowMoveModal(true)}
                  disabled={bulkBusy !== null || moveFolders.length === 0}
                  className="inline-flex items-center justify-center rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] px-3 py-2.5 text-sm font-medium text-[hsl(var(--foreground))] transition-colors hover:bg-[hsl(var(--bg3))]/70 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("folder.bulkMove")}
                </button>
                <button
                  type="button"
                  onClick={() => void handleRemoveSelected()}
                  disabled={bulkBusy !== null}
                  className="inline-flex items-center justify-center rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-500/30 dark:bg-red-500/10 dark:hover:bg-red-500/15"
                >
                  {t("folder.bulkRemove")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddMaterials && folderId && (
        <AddMaterialsModal
          folderId={folderId}
          decks={decks}
          existingDeckIds={folderDeckIds}
          onClose={() => setShowAddMaterials(false)}
          onSaved={() => {
            setShowAddMaterials(false);
            void loadFolder();
            onRefreshFolders?.();
          }}
        />
      )}

      {showMoveModal && onCreateFolder && (
        <MoveDecksToFolderModal
          isOpen={showMoveModal}
          onClose={() => setShowMoveModal(false)}
          selectedCount={selectedCount}
          folders={moveFolders}
          onCreateFolder={handleCreateFolderForMove}
          onSubmit={handleMoveSelected}
        />
      )}

      {isTagModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setIsTagModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
              {t("folder.tags_title")}
            </h3>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder={t("folder.tags_placeholder")}
              className="mb-4 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 outline-none transition-all placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-gray-100 dark:placeholder-gray-500"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setIsTagModalOpen(false);
                  setTagInput("");
                }}
                className="rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600"
              >
                {t("folder.cancel")}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleSaveTags();
                }}
                className="rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white transition-colors hover:bg-indigo-700"
              >
                {t("folder.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmState && (
        <AlertDialog open={confirmState.open} onOpenChange={(open) => !open && closeConfirm()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmState.title}</AlertDialogTitle>
              {confirmState.description && (
                <AlertDialogDescription>{confirmState.description}</AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("folder.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    await confirmState.onConfirm();
                    closeConfirm();
                  } catch {
                    /* Callbacks handle their own toasts; keep dialog open so user can retry or cancel */
                  }
                }}
              >
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <Dialog open={editFolderOpen} onOpenChange={setEditFolderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("folder.edit")}</DialogTitle>
          </DialogHeader>
          <Input
            value={editFolderValue}
            onChange={(e) => setEditFolderValue(e.target.value)}
            placeholder={t("folder.edit")}
            onKeyDown={(e) => e.key === "Enter" && void handleEditFolderSubmit()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFolderOpen(false)}>
              {t("folder.cancel")}
            </Button>
            <Button
              onClick={() => void handleEditFolderSubmit()}
              disabled={!editFolderValue?.trim() || editFolderValue.trim() === folderTitle}
            >
              {t("folder.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
